import { DownloadIcon, PlayIcon } from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Button,
  Card,
  Code,
  Flex,
  Heading,
  IconButton,
  Link,
  Text,
} from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonBackArrow } from 'components/common/buttons/back-arrow';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTableHoC } from 'components/common/table';
import useBurnInTestStore from 'components/sections/admin-portal/machines/dialogs/settings/machine-burn-in/use-burn-in-test-store';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import env from 'config';
import { differenceInMinutes, isAfter, isBefore, parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import {
  MachineSettingsQueryParams,
  MachineSettingsSection,
} from 'enums/machine-settings';
import { TABLES } from 'enums/tables';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import {
  LOCAL_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IError } from 'lib_ts/interfaces/common/i-error';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import {
  IErrorSummary,
  IProcessedDict,
} from 'lib_ts/interfaces/i-burn-in-test';
import { compact, isEqual, isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { paths } from 'routes/paths';
import { AdminBurnInTestsService } from 'services/admin/machine-burn-in-tests.service';
import { AdminMachinesService } from 'services/admin/machines.service';

const COMPONENT_NAME = 'MachineBurnInReport';

const formatDate = (value: Date | string | undefined) =>
  value
    ? format(
        typeof value === 'string' ? parseISO(value) : value,
        LOCAL_DATETIME_FORMAT,
        {
          timeZone: LOCAL_TIMEZONE,
        }
      )
    : null;

const getShotLinkUrl = (machineID: string, id: string) => {
  const params = new URLSearchParams({
    [MachineSettingsQueryParams.MachineID]: machineID,
    [MachineSettingsQueryParams.SectionID]: MachineSettingsSection.ShotHistory,
    [MachineSettingsQueryParams.ShotID]: id,
  });
  return `${env.self_url}${paths.admin.machines}?${params.toString()}`;
};

const getErrorLinkUrl = (machineID: string, id: string) => {
  const params = new URLSearchParams({
    [MachineSettingsQueryParams.MachineID]: machineID,
    [MachineSettingsQueryParams.SectionID]: MachineSettingsSection.Errors,
    [MachineSettingsQueryParams.ErrorID]: id,
  });
  return `${env.self_url}${paths.admin.machines}?${params.toString()}`;
};

const getErrorsLinkUrl = (
  machineID: string,
  start: Nullable<string>,
  end: Nullable<string>
) => {
  if (!start) {
    return undefined;
  }

  if (!end) {
    return undefined;
  }

  const params = new URLSearchParams({
    [MachineSettingsQueryParams.MachineID]: machineID,
    [MachineSettingsQueryParams.SectionID]: MachineSettingsSection.Errors,
    [MachineSettingsQueryParams.ErrorDateStart]: start,
    [MachineSettingsQueryParams.ErrorDateEnd]: end,
  });
  return `${env.self_url}${paths.admin.machines}?${params.toString()}`;
};

interface ITimelineSummaryRow {
  _id: string;
  date: Date;
  shotID: Nullable<string>;
  errors: IErrorSummary[];
}

const TimelineSummaryTable = (props: {
  dataLabel: string;
  processed: IProcessedDict;
  errors: IErrorSummary[];
  shots?: IProcessedDict;
}) => {
  const machine = useMachinesPortalStore((s) => s.machine);

  const rows = useMemo(() => {
    const output: ITimelineSummaryRow[] = compact(
      Object.keys(props.processed).map((key) => {
        const processedDate = props.processed[key];

        if (!processedDate) {
          return undefined;
        }

        const o: ITimelineSummaryRow = {
          _id: key,
          date: parseISO(processedDate),
          shotID: props.shots?.[key] ?? null,
          errors: props.errors.filter((m) => m._created),
        };

        return o;
      })
    );

    // sort output by ascending date values
    output.sort((a, b) => (isBefore(a.date, b.date) ? -1 : 1));

    // mixin errors based on date ranges
    let prevEntryDate: Date | undefined = undefined;

    for (const entry of output) {
      entry.errors = props.errors.filter((m) => {
        const errDate = parseISO(m._created);

        const isAfterPrev = !prevEntryDate || isAfter(errDate, prevEntryDate);
        const isBeforeCurr = isBefore(errDate, entry.date);

        return isAfterPrev && isBeforeCurr;
      });

      // shift the date up
      prevEntryDate = entry.date;
    }

    return output;
  }, [props.processed, props.shots, props.errors]);

  const columns = useMemo(
    (): ITypedTableColumn<ITimelineSummaryRow>[] => [
      {
        label: 'Timestamp',
        key: 'date',
        formatFn: (m) => formatDate(m.date),
      },
      {
        label: props.dataLabel,
        key: '_id',
        formatFn: (m) => (
          <Code
            className="cursor-pointer"
            onClick={() => {
              navigator.clipboard.writeText(m._id);

              NotifyHelper.info({
                message_md: 'Copied to clipboard!',
              });
            }}
          >
            {m._id}
          </Code>
        ),
      },
      {
        label: 'Shot History',
        key: 'shotID',
        invisible: !props.shots,
        formatFn: (m) => {
          if (!machine?.machineID) {
            return null;
          }

          if (!m.shotID) {
            return null;
          }

          const url = getShotLinkUrl(machine.machineID, m.shotID);

          if (!url) {
            return null;
          }

          return (
            <IconButton
              variant="soft"
              size="1"
              onClick={() => window.open(url, '_blank')}
            >
              <PlayIcon />
            </IconButton>
          );
        },
      },
      {
        label: 'Errors',
        key: 'errors',
        formatFn: (m) => {
          if (!machine?.machineID) {
            return null;
          }

          return (
            <ol>
              {m.errors.map((err) => (
                <li key={err._id}>
                  <Link
                    target="_blank"
                    href={getErrorLinkUrl(machine.machineID, err._id)}
                  >
                    {err.errorID}
                  </Link>
                </li>
              ))}
            </ol>
          );
        },
      },
    ],
    [machine?.machineID, props.dataLabel, props.shots]
  );

  const pagination: ITablePageable = {
    paginationID: PaginationID.BurnInTestTimelineSummary,
    enablePagination: true,
    pageSizes: TABLES.PAGE_SIZES.LG,
    total: Object.keys(props.processed).length,
  };

  return (
    <CommonTableHoC
      identifier="BurnInTestTimelineSummaryTable"
      dataTestID="BurnInTestTimelineSummaryTable"
      displayColumns={columns}
      displayData={rows}
      {...pagination}
    />
  );
};

export const MachineBurnInReport = () => {
  const machine = useMachinesPortalStore((s) => s.machine);

  const tests = useBurnInTestStore((s) => s.tests);
  const activeTestID = useBurnInTestStore((s) => s.activeTestID);

  const activeTest = useMemo(
    () => tests.find((m) => m._id === activeTestID),
    [tests, activeTestID]
  );

  const [errors, setErrors] = useState<IError[]>([]);

  useEffect(() => {
    if (!machine?.machineID) {
      return;
    }

    if (!activeTest?.start) {
      return;
    }

    if (!activeTest.end) {
      return;
    }

    AdminMachinesService.getInstance()
      .getErrors({
        machineID: machine.machineID,
        start_date: activeTest.start,
        end_date: activeTest.end,
      })
      .then((results) => {
        setErrors(results.data);
      });
  }, [machine?.machineID, activeTest?.start, activeTest?.end]);

  // check if we need to summarize errors on the test
  useEffect(() => {
    if (errors.length === 0) {
      // skip for default state and if there were no errors
      return;
    }

    if (!activeTest?._id) {
      return;
    }

    const nextErrors: IErrorSummary[] = compact(
      errors.map((m) =>
        m.errorID
          ? {
              _id: m._id,
              _created: m._created,
              errorID: m.errorID,
            }
          : undefined
      )
    );

    if (
      isEqual(
        // what was loaded from the server
        nextErrors.map((m) => m._id).sort(),
        // what was found on the record
        activeTest.errors.map((m) => m._id).sort()
      )
    ) {
      // no change
      return;
    }

    AdminBurnInTestsService.getInstance()
      .putOne(activeTest._id, {
        errors: nextErrors,
      })
      .then((updatedTest) => {
        if (!updatedTest) {
          console.warn(`${COMPONENT_NAME}: failed to update test`);
          return;
        }

        console.debug(`${COMPONENT_NAME}: updating active test`, updatedTest);
        useBurnInTestStore.getState().updateTest(updatedTest);
      });
  }, [activeTest?._id, activeTest?.errors, errors]);

  const durationMin = useMemo(() => {
    if (!activeTest || !activeTest.start || !activeTest.end) {
      return null;
    }

    return differenceInMinutes(
      parseISO(activeTest.end),
      parseISO(activeTest.start)
    );
  }, [activeTest]);

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonBackArrow
        onClick={() => useBurnInTestStore.setState({ activeTestID: null })}
      />

      {activeTest && (
        <Card size="3">
          <Flex direction="column" gap="4">
            <Flex gap="2" justify="between" align="center">
              <Heading className="capitalize" truncate>
                {activeTest?.type} Test Report
              </Heading>

              {activeTest && activeTest.start && activeTest.end && (
                <Badge>
                  {formatDate(activeTest.start)} - {formatDate(activeTest.end)}
                </Badge>
              )}
            </Flex>

            <Box>
              <Button
                variant={RADIX.BUTTON.VARIANT.SECONDARY}
                onClick={() => {
                  if (!activeTest) {
                    return;
                  }

                  const blob = new Blob([JSON.stringify(activeTest, null, 2)], {
                    type: 'text/json',
                  });

                  MiscHelper.saveAs(
                    blob,
                    `${activeTest.type}-test-${activeTest._id}.json`
                  );
                }}
              >
                <DownloadIcon /> Download
              </Button>
            </Box>

            <Flex direction="column" gap="2">
              {activeTest.rangeTest && !isNil(durationMin) && (
                <Text>
                  Checked {Object.keys(activeTest.rangeTest.processed).length}{' '}
                  different machine states in {durationMin} min.
                </Text>
              )}

              {activeTest.firingTest && !isNil(durationMin) && (
                <Text>
                  Fired {Object.keys(activeTest.firingTest.processed).length}{' '}
                  different pitches in {durationMin} min.
                </Text>
              )}

              {machine?.machineID && (
                <Text>
                  During this test, the machine reported{' '}
                  <Link
                    target="_blank"
                    href={getErrorsLinkUrl(
                      machine.machineID,
                      activeTest.start,
                      activeTest.end
                    )}
                  >
                    {errors.length} error(s)
                  </Link>
                  .
                </Text>
              )}

              {activeTest.rangeTest?.processed && (
                <TimelineSummaryTable
                  dataLabel="MS ID"
                  processed={activeTest.rangeTest.processed}
                  errors={activeTest.errors}
                />
              )}

              {activeTest.firingTest?.processed && (
                <TimelineSummaryTable
                  dataLabel="Pitch ID"
                  processed={activeTest.firingTest.processed}
                  shots={activeTest.firingTest.shots}
                  errors={activeTest.errors}
                />
              )}
            </Flex>
          </Flex>
        </Card>
      )}
    </ErrorBoundary>
  );
};
